import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Wine, Sparkles, LogIn, Eye, EyeOff, KeyRound, CheckCircle2, Send,
  ArrowLeft, Coffee, Pizza, Beer, Building, ChevronRight, Loader2, User, Sprout
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMO_ICONS = {
  cocktailbar: Wine,
  pizzeria: Pizza,
  cafe: Coffee,
  biergarten: Beer,
  hotel: Building,
  irish_pub_de: Beer,
  irish_pub_at: Beer,
  vegan_de: Sprout,
  vegan_at: Sprout,
};

const DEMO_COLORS = {
  cocktailbar: "from-violet-500 to-purple-600",
  pizzeria: "from-orange-500 to-red-500",
  cafe: "from-amber-500 to-yellow-600",
  biergarten: "from-yellow-500 to-green-500",
  hotel: "from-blue-500 to-cyan-500",
  irish_pub_de: "from-emerald-600 to-green-700",
  irish_pub_at: "from-emerald-500 to-teal-600",
  vegan_de: "from-lime-500 to-green-600",
  vegan_at: "from-emerald-500 to-lime-600",
};

export const Login = () => {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("main"); // main | demos | login
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoLoadingType, setDemoLoadingType] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [demoTypes, setDemoTypes] = useState([]);

  // Password Reset
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/demo/types`).then(res => {
      setDemoTypes(res.data.types || []);
    }).catch(() => {});
  }, []);

  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: username.trim(), password
      }, { withCredentials: true });
      setUser(response.data);
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleDemoStart = async (demoType) => {
    setDemoLoading(true);
    setDemoLoadingType(demoType);
    try {
      const response = await axios.post(`${API}/demo/start?demo_type=${demoType}`, {}, { withCredentials: true });
      if (response.data.success) {
        await new Promise(resolve => setTimeout(resolve, 500));
        window.location.href = '/dashboard';
      }
    } catch (error) {
      toast.error("Demo konnte nicht gestartet werden");
    } finally {
      setDemoLoading(false);
      setDemoLoadingType(null);
    }
  };

  const handlePasswordResetRequest = async (e) => {
    e.preventDefault();
    if (!resetUsername.trim()) { toast.error("Bitte Benutzername eingeben"); return; }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset-request`, { username: resetUsername.trim() });
      setResetSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Anfrage");
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetDialog = () => {
    setShowResetDialog(false);
    setResetUsername("");
    setResetSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <img src="/logo.png" alt="MargeVio" className="h-16 mx-auto mb-3 cursor-pointer" onClick={() => navigate("/")} />
          <p className="text-slate-400 text-sm">Gastro Kalkulator</p>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-lg border border-slate-700 p-6 shadow-2xl rounded-xl">
          {/* ========== MAIN VIEW ========== */}
          {view === "main" && (
            <>
              <div className="text-center mb-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-violet-500/10 text-violet-400 border border-violet-500/30 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse"></span>
                  BETA VERSION
                </span>
                <h2 className="text-xl font-semibold text-white">Willkommen bei MargeVio</h2>
                <p className="text-sm text-slate-400 mt-1">Waehle eine Option um fortzufahren</p>
              </div>

              <div className="space-y-3">
                {/* Demo starten */}
                <button
                  onClick={() => setView("demos")}
                  className="w-full p-4 bg-gradient-to-r from-violet-600/20 to-purple-600/20 border border-violet-500/40 rounded-xl hover:border-violet-400/60 transition-all group text-left"
                  data-testid="goto-demos-btn"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-violet-500/20 rounded-lg group-hover:bg-violet-500/30 transition-colors">
                        <Sparkles size={22} className="text-violet-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Demo testen</p>
                        <p className="text-xs text-slate-400">5 Betriebsarten mit realistischen Daten</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-500 group-hover:text-violet-400 transition-colors" />
                  </div>
                </button>

                {/* Eigener Zugriff */}
                <button
                  onClick={() => setView("login")}
                  className="w-full p-4 bg-slate-700/30 border border-slate-600/50 rounded-xl hover:border-slate-500/60 transition-all group text-left"
                  data-testid="goto-login-btn"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-slate-600/30 rounded-lg group-hover:bg-slate-600/50 transition-colors">
                        <User size={22} className="text-slate-300" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Eigener Zugriff</p>
                        <p className="text-xs text-slate-400">Mit Benutzername & Passwort anmelden</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-500 group-hover:text-slate-300 transition-colors" />
                  </div>
                </button>

                {/* Zurueck zur Homepage */}
                <button
                  onClick={() => navigate("/")}
                  className="w-full p-3 text-slate-400 hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
                  data-testid="goto-homepage-btn"
                >
                  <ArrowLeft size={16} />
                  Zurueck zur Homepage
                </button>
              </div>

              {/* Pricing Link */}
              <div className="mt-5 text-center">
                <a href="/pricing" className="text-xs text-violet-400 hover:text-violet-300 font-medium hover:underline">
                  Alle Plaene ansehen
                </a>
              </div>
            </>
          )}

          {/* ========== DEMOS VIEW ========== */}
          {view === "demos" && (
            <>
              <div className="flex items-center gap-3 mb-5">
                <button onClick={() => setView("main")} className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors" data-testid="back-from-demos">
                  <ArrowLeft size={18} className="text-slate-400" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-white">Demo auswaehlen</h2>
                  <p className="text-xs text-slate-400">Alle Demos enthalten 365 Tage realistische Daten</p>
                </div>
              </div>

              <div className="space-y-2.5">
                {demoTypes.map((demo) => {
                  const DemoIcon = DEMO_ICONS[demo.id] || Wine;
                  const gradient = DEMO_COLORS[demo.id] || "from-slate-500 to-slate-600";
                  const isLoading = demoLoadingType === demo.id;

                  return (
                    <button
                      key={demo.id}
                      onClick={() => handleDemoStart(demo.id)}
                      disabled={demoLoading}
                      className="w-full p-3.5 bg-slate-700/30 border border-slate-600/40 rounded-xl hover:border-slate-500/60 transition-all group text-left disabled:opacity-50"
                      data-testid={`demo-${demo.id}-btn`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 bg-gradient-to-br ${gradient} rounded-lg flex-shrink-0`}>
                          {isLoading ? (
                            <Loader2 size={20} className="text-white animate-spin" />
                          ) : (
                            <DemoIcon size={20} className="text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium text-sm">{demo.name}</p>
                            {demo.neu && (
                              <span className="text-[9px] px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded font-semibold">NEU</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {demo.tags?.slice(0, 3).map((tag, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-600/50 text-slate-400 rounded">{tag}</span>
                            ))}
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-slate-600 group-hover:text-slate-400 flex-shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="text-[10px] text-slate-500 text-center mt-4">
                Demo-Daten werden nach 24h automatisch geloescht
              </p>
            </>
          )}

          {/* ========== LOGIN VIEW ========== */}
          {view === "login" && (
            <>
              <div className="flex items-center gap-3 mb-5">
                <button onClick={() => setView("main")} className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors" data-testid="back-from-login">
                  <ArrowLeft size={18} className="text-slate-400" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-white">Anmelden</h2>
                  <p className="text-xs text-slate-400">Mit deinem Konto einloggen</p>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-xs uppercase tracking-wide text-slate-400">Benutzername</Label>
                  <Input
                    id="username" type="text" value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Benutzername" required
                    className="bg-slate-700 border-slate-600 rounded-lg py-5 text-white placeholder:text-slate-500"
                    data-testid="login-username" autoComplete="username"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wide text-slate-400">Passwort</Label>
                  <div className="relative">
                    <Input
                      id="password" type={showPassword ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Passwort" required
                      className="bg-slate-700 border-slate-600 rounded-lg py-5 pr-10 text-white placeholder:text-slate-500"
                      data-testid="login-password" autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300" tabIndex={-1}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && <p className="text-sm text-red-400 text-center" data-testid="login-error">{error}</p>}

                <Button type="submit" disabled={loginLoading}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-6 flex items-center justify-center gap-3"
                  data-testid="login-submit-btn">
                  {loginLoading ? (
                    <><Loader2 size={18} className="animate-spin" /> Anmeldung...</>
                  ) : (
                    <><LogIn size={18} /> Anmelden</>
                  )}
                </Button>
              </form>

              <div className="text-center mt-3">
                <button type="button" onClick={() => setShowResetDialog(true)}
                  className="text-sm text-blue-400 hover:text-blue-300 hover:underline" data-testid="forgot-password-link">
                  Passwort vergessen?
                </button>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-5">
          <p className="text-xs text-slate-500">Gastro Kalkulator</p>
          <p className="text-xs text-violet-400 font-medium">by Gian-Luca</p>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={closeResetDialog}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <KeyRound size={20} className="text-blue-400" /> Passwort vergessen?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Geben Sie Ihren Benutzernamen ein. Der Admin wird sich bei Ihnen melden.
            </DialogDescription>
          </DialogHeader>
          {resetSuccess ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 size={24} className="text-emerald-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Anfrage gesendet!</h3>
              <p className="text-sm text-slate-400 mb-4">Der Admin wird Ihre Anfrage pruefen.</p>
              <Button onClick={closeResetDialog} className="w-full bg-violet-600">Verstanden</Button>
            </div>
          ) : (
            <form onSubmit={handlePasswordResetRequest} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-slate-400">Benutzername</Label>
                <Input type="text" value={resetUsername} onChange={(e) => setResetUsername(e.target.value)}
                  placeholder="Ihr Benutzername" required className="bg-slate-700 border-slate-600 text-white" data-testid="reset-username-input" />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1 border-slate-600" onClick={closeResetDialog}>Abbrechen</Button>
                <Button type="submit" disabled={resetLoading} className="flex-1 bg-blue-600 hover:bg-blue-700 gap-2" data-testid="reset-request-btn">
                  <Send size={16} /> {resetLoading ? "Senden..." : "Anfragen"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
